import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { AlertTriangle, Upload, CheckCircle2, Loader2, Layers, X } from 'lucide-react';
import PaidFeaturePaywall from '@/components/PaidFeaturePaywall';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = 'totalproductionsolutions@gmail.com';

/**
 * FishtalkUploadPanel
 * --------------------
 * Multi-file Fishtalk direct upload — no cap on the number of files or
 * cages. Same UX as the AquaManager panel — only differences are the
 * endpoint (/api/fishtalk/*), the info copy (Fishtalk provides harvest
 * avg weight directly so no calculation is needed), and the persisted
 * fish-number field name (`fishtalk_fish_number`).
 *
 * All cages in a batch share species, genetic origin, and FCR.
 */
export default function FishtalkUploadPanel({
  recordName,
  species,
  geneticOrigin,
  modelAdjustmentPercent,
  onUploadSuccess,
  isReadOnly,
  costLabel,
}) {
  const { token, user } = useAuth();
  const isAdmin = user?.is_admin === true && user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  // Unlimited-access users bypass the paywall (kept in sync with backend
  // require_admin_paid_feature via UNLIMITED_ACCESS_EMAILS).
  const hasPaidFeatureAccess = isAdmin || user?.is_unlimited === true;
  const [files, setFiles] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cageEdits, setCageEdits] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // Batch-wide duplicate-resolution prompt. Populated when the backend
  // returns HTTP 409 `{duplicate_name: true, duplicate_cages}` — see
  // RULE-IMP-DUP-001. The operator picks Overwrite All / Keep Both /
  // Cancel and we resubmit with the chosen `duplicate_action`.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null);

  const cageKey = (c) => `${c.file_index}::${c.sheet_name}`;

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const combined = [...files, ...picked.map((f) => ({ file: f, name: f.name }))];
    setFiles(combined);
    setPreview(null);
    setCageEdits({});
    setError('');
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setPreview(null);
    setCageEdits({});
  };

  const readAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildFilesPayload = async () =>
    Promise.all(files.map(async (entry) => ({
      file_content: await readAsBase64(entry.file),
      filename: entry.name,
    })));

  const runPreview = async () => {
    if (!files.length) {
      toast.error('Please pick at least one Fishtalk Excel file');
      return;
    }
    setPreviewing(true);
    setError('');
    try {
      const filesPayload = await buildFilesPayload();
      const resp = await axios.post(
        `${BACKEND_URL}/api/fishtalk/preview`,
        { files: filesPayload },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreview(resp.data);
      const seeds = {};
      (resp.data.cages || []).forEach((c) => { seeds[cageKey(c)] = c.cage_name; });
      setCageEdits(seeds);
      setShowConfirm(true);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Preview failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setPreviewing(false);
    }
  };

  const runUpload = async (duplicateAction = null) => {
    if (!files.length || !preview) return;
    setUploading(true);
    setError('');
    try {
      const filesPayload = await buildFilesPayload();
      const cages = (preview.cages || []).map((c) => ({
        file_index: c.file_index,
        sheet_name: c.sheet_name,
        cage_name: (cageEdits[cageKey(c)] || c.cage_name || c.sheet_name).trim(),
      }));
      const resp = await axios.post(
        `${BACKEND_URL}/api/fishtalk/upload`,
        {
          files: filesPayload,
          cages,
          species,
          genetic_origin: (species === 'Seabass' || species === 'Tilapia' || species === 'Tripletail')
            ? null
            : geneticOrigin,
          temperature_region: 'User uploaded temperatures',
          model_adjustment_percent: modelAdjustmentPercent ?? 100,
          name_prefix: recordName?.trim() || null,
          ...(duplicateAction ? { duplicate_action: duplicateAction } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const batch = resp.data;
      toast.success(`${batch.cage_count} cage${batch.cage_count > 1 ? 's' : ''} imported and analysed successfully`);
      setShowConfirm(false);
      setFiles([]);
      setPreview(null);
      setCageEdits({});
      if (onUploadSuccess && batch.cages?.length) {
        if (batch.cages.length > 1) {
          toast.info(`Showing "${batch.cages[batch.cages.length - 1].name || ''}". The other ${batch.cages.length - 1} cage(s) are available in Saved Analyses.`);
        }
        onUploadSuccess(batch.cages[batch.cages.length - 1]);
      }
    } catch (err) {
      // Exact-name duplicate is NOT an upload failure — it's a
      // resolution state. Route it to the confirmation dialog instead
      // of the red toast so the operator can Overwrite All / Keep Both.
      // See RULE-IMP-DUP-001.
      const detailObj = err.response?.data?.detail;
      if (err.response?.status === 409 && detailObj && detailObj.duplicate_name) {
        setDuplicatePrompt({
          cages: detailObj.duplicate_cages || [],
          message: detailObj.message || 'A record with this name already exists. Overwrite it with this upload?',
        });
        return; // do not surface as an error
      }
      const detail = typeof detailObj === 'string' ? detailObj : (detailObj?.message || err.message || 'Upload failed');
      setError(detail);
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const resolveDuplicate = async (action) => {
    setDuplicatePrompt(null);
    await runUpload(action);
  };
  const cancelDuplicate = () => setDuplicatePrompt(null);

  const cages = preview?.cages || [];

  // Non-admin users see a paywall in place of the upload UI. The backend
  // also gates /api/fishtalk/* with a matching 403 so direct API calls
  // fail with the same message. Unlimited-access users bypass the paywall.
  if (!hasPaidFeatureAccess) {
    return (
      <div data-testid="fishtalk-upload-panel">
        <PaidFeaturePaywall featureName="Fishtalk Direct Upload" tone="indigo" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="fishtalk-upload-panel">
      <div
        className="rounded-md border border-indigo-300 bg-indigo-50 px-3 py-2 text-xs text-indigo-900 flex items-start gap-2"
        data-testid="fishtalk-info-notice"
      >
        <Layers className="w-4 h-4 mt-0.5 flex-shrink-0 text-indigo-600" />
        <div>
          <p className="font-semibold mb-0.5">
            Direct upload — no cap on the number of cages
          </p>
          <p>
            Upload <strong>any number of Fishtalk export files at once</strong>
            {' '}(<span className="font-mono">.xlsx</span> or <span className="font-mono">.xls</span>).
            Each file is one cage; the cage name defaults to the Excel sheet label
            (or the filename if the sheet is generic, e.g. <span className="font-mono">Sheet1</span>).
            All cages in this batch will use the same species, genetic origin, and FCR
            model set above. <strong>Harvest average weight is taken directly from the
            export</strong> — no calculation needed.
          </p>
          <p
            className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] leading-snug text-amber-800"
            data-testid="ft-species-grouping-note"
          >
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
            <span>
              <strong>Group uploads by species.</strong> Do not mix multiple species in a single
              upload — every cage in this batch is analysed against the one species / genetic
              origin / FCR model chosen above. Upload each species as its own batch.
            </span>
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          id="ftFileUpload"
          type="file"
          accept=".xlsx,.xls"
          multiple
          onChange={handleFiles}
          className="bg-white flex-1"
          data-testid="ft-file-input"
        />
        <Button
          onClick={runPreview}
          disabled={!files.length || previewing || isReadOnly}
          className="bg-indigo-700 hover:bg-indigo-800 text-white rounded-full px-6 shadow-md hover:shadow-lg transition-all"
          data-testid="ft-preview-btn"
        >
          {previewing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing {files.length} file{files.length > 1 ? 's' : ''}…</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Preview {files.length || ''} Fishtalk File{files.length === 1 ? '' : 's'}</>
          )}
        </Button>
      </div>

      {files.length > 0 && (
        <div className="space-y-1.5" data-testid="ft-selected-files">
          <p className="text-xs text-slate-500">
            {files.length} file{files.length > 1 ? 's' : ''} selected
          </p>
          <ul className="space-y-1">
            {files.map((entry, i) => (
              <li
                key={`${entry.name}-${i}`}
                className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-xs"
                data-testid={`ft-selected-file-${i}`}
              >
                <span className="font-mono text-emerald-900 truncate">{entry.name}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-emerald-700 hover:text-red-600 ml-2"
                  aria-label={`Remove ${entry.name}`}
                  data-testid={`ft-remove-file-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 whitespace-pre-line" data-testid="ft-error-msg">{error}</p>
        </div>
      )}

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Confirm Fishtalk Import
              {preview?.cage_count > 0 && (
                <span
                  className="text-xs font-normal text-slate-500 bg-slate-100 rounded-full px-2 py-0.5"
                  data-testid="ft-cage-count-badge"
                >
                  {preview.cage_count} cage{preview.cage_count > 1 ? 's' : ''}
                  {preview.file_count > 1 && ` · ${preview.file_count} files`}
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              Detected format: <strong>Fishtalk Export</strong>. All cages below will be
              analysed as <strong>{species}{species === 'Seabream' ? ` · ${geneticOrigin || 'Greek'}` : ''}</strong>
              {' '}using the FCR model configured above. Cage names default to the sheet
              label (or the filename if the sheet name is generic) — edit any field below
              before confirming.
            </DialogDescription>
          </DialogHeader>

          {recordName?.trim() && (
            <p className="text-xs text-slate-500" data-testid="ft-name-prefix-note">
              Saved record names will be prefixed with <span className="font-mono font-semibold">{recordName.trim()}</span>.
            </p>
          )}

          <div className="space-y-4" data-testid="ft-preview-cages">
            {cages.map((cage, ci) => (
              <CagePreviewCard
                key={cageKey(cage)}
                cage={cage}
                index={ci}
                editedName={cageEdits[cageKey(cage)] ?? cage.cage_name}
                onRename={(v) => setCageEdits((prev) => ({ ...prev, [cageKey(cage)]: v }))}
              />
            ))}
          </div>

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={uploading} data-testid="ft-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={() => runUpload()}
              disabled={uploading || isReadOnly || cages.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ft-confirm-upload-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running analysis for {cages.length} cage{cages.length > 1 ? 's' : ''}…</>
              ) : (
                <>Confirm &amp; Run Analysis ({cages.length} cage{cages.length > 1 ? 's' : ''}{costLabel || ''})</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exact-name duplicate confirmation — replaces the red failure
          toast when the backend rejects with HTTP 409
          `{duplicate_name: true, duplicate_cages}`. Rule: no partial
          save before the operator resolves (RULE-IMP-DUP-001). */}
      <AlertDialog
        open={!!duplicatePrompt}
        onOpenChange={(open) => { if (!open) cancelDuplicate(); }}
      >
        <AlertDialogContent data-testid="ft-duplicate-name-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {duplicatePrompt?.cages?.length > 1 ? 'Some names already exist' : 'Analysis name already exists'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {duplicatePrompt?.message}
              {duplicatePrompt?.cages?.length > 0 && (
                <ul className="mt-2 text-xs list-disc list-inside text-slate-700 max-h-32 overflow-y-auto">
                  {duplicatePrompt.cages.map((c) => (
                    <li key={c.cage_name}><span className="font-mono">{c.existing_name || c.cage_name}</span></li>
                  ))}
                </ul>
              )}
              <span className="block mt-2 text-xs text-slate-500">
                Overwrite All replaces the existing record(s) and invalidates any saved analysis on them.
                Keep Both preserves the existing record(s) and creates new one(s) with a numeric suffix.
                Cancel saves nothing.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={cancelDuplicate}
              data-testid="ft-duplicate-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => resolveDuplicate('keep_both')}
              data-testid="ft-duplicate-keep-both-btn"
            >
              Keep Both
            </Button>
            <AlertDialogAction
              onClick={() => resolveDuplicate('overwrite')}
              data-testid="ft-duplicate-overwrite-btn"
            >
              {duplicatePrompt?.cages?.length > 1 ? 'Overwrite All' : 'Overwrite Existing'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CagePreviewCard({ cage, index, editedName, onRename }) {
  const s = cage.summary || {};
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
      data-testid={`ft-cage-card-${index}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          {cage.filename && (
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-mono">
              File: {cage.filename}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-mono">
            Sheet: {cage.sheet_name}
          </span>
          {cage.warnings?.length > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
              {cage.warnings.length} warning{cage.warnings.length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500" htmlFor={`ft-cage-name-${index}`}>Cage name</label>
          <Input
            id={`ft-cage-name-${index}`}
            value={editedName}
            onChange={(e) => onRename(e.target.value)}
            className="h-7 text-sm w-56"
            data-testid={`ft-cage-name-input-${index}`}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
        <MiniKpi label="Rows" value={s.rows_imported} />
        <MiniKpi label="Date range" value={s.date_range ? `${s.date_range.start} → ${s.date_range.end}` : '—'} mono small />
        <MiniKpi label="Feed (kg)" value={s.total_feed_kg?.toLocaleString?.()} />
        <MiniKpi label="Mortality" value={s.total_mortality?.toLocaleString?.()} />
        <MiniKpi label="Samples" value={s.routine_sample_points} />
        <MiniKpi label="Harvests" value={s.harvest_events} />
      </div>

      <div className="text-[11px] text-slate-600 bg-slate-50 rounded-md px-3 py-1.5">
        Stocking: <strong>{s.initial_fish_no?.toLocaleString?.()}</strong> fish
        @ <strong>{s.initial_avg_wt_g} g</strong>. Last Fishtalk Fish No:{' '}
        <strong className="font-mono">{cage.fishtalk_fish_number?.toLocaleString?.()}</strong>{' '}
        on {cage.fishtalk_fish_number_date}.
      </div>

      {cage.harvest_events?.length > 0 && (
        <details className="text-xs" data-testid={`ft-cage-harvest-${index}`}>
          <summary className="cursor-pointer text-slate-600 font-semibold py-1">
            Harvest events ({cage.harvest_events.length}) — avg weight taken directly from Fishtalk
          </summary>
          <div className="overflow-x-auto mt-1">
            <table className="w-full text-[11px] border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-2 py-1 border">Date</th>
                  <th className="text-right px-2 py-1 border">Fish</th>
                  <th className="text-right px-2 py-1 border">Biomass (kg)*</th>
                  <th className="text-right px-2 py-1 border">Avg weight (g)</th>
                </tr>
              </thead>
              <tbody>
                {cage.harvest_events.map((ev, i) => (
                  <tr key={i} className="even:bg-slate-50">
                    <td className="px-2 py-1 border font-mono">{ev.date}</td>
                    <td className="px-2 py-1 border text-right">{ev.harvest_no.toLocaleString()}</td>
                    <td className="px-2 py-1 border text-right">{ev.harvest_kg.toLocaleString()}</td>
                    <td className="px-2 py-1 border text-right font-semibold">{ev.avg_weight_g}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-500 italic mt-1">
              * Biomass derived as avg × count / 1000 (Fishtalk does not export biomass directly).
            </p>
          </div>
        </details>
      )}

      {cage.warnings?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-amber-800 font-semibold py-1">
            Warnings ({cage.warnings.length})
          </summary>
          <ul className="list-disc list-outside pl-5 text-amber-800 mt-1 space-y-0.5">
            {cage.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </details>
      )}

      {cage.rejected_rows?.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-red-800 font-semibold py-1">
            Rejected rows ({cage.rejected_rows.length})
          </summary>
          <ul className="list-disc list-outside pl-5 text-red-800 mt-1 space-y-0.5 max-h-32 overflow-y-auto">
            {cage.rejected_rows.map((r, i) => (
              <li key={i}>Row {r.row}: {r.reason}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function MiniKpi({ label, value, mono, small }) {
  return (
    <div className="rounded-md bg-slate-50 px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`font-semibold text-slate-900 ${mono ? 'font-mono' : ''} ${small ? 'text-[11px]' : 'text-sm'}`}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </p>
    </div>
  );
}
